import { buildAuthHeader, clearAuthSession, getStoredAuthToken, resolveCareersBaseUrl } from "./backend-auth";

export const API_TIMEOUT_MS = 10_000;

export class ApiTimeoutError extends Error {
  constructor() {
    super("La solicitud tardó demasiado. Intenta nuevamente.");
    this.name = "ApiTimeoutError";
  }
}

export async function callCareersApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});

  if (!headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const authHeader = buildAuthHeader(getStoredAuthToken());
  if (authHeader && !headers.has("Authorization")) {
    headers.set("Authorization", authHeader);
  }

  const baseUrl = resolveCareersBaseUrl().replace(/\/+$/, "") + "/";
  const requestPath = /^https?:\/\//i.test(path) ? path : path.replace(/^\/+/, "");
  const url = new URL(requestPath, baseUrl).toString();

  const timeoutController = new AbortController();
  const requestSignal = init.signal;
  if (requestSignal) {
    requestSignal.addEventListener("abort", () => timeoutController.abort(), { once: true });
  }
  const timeoutId = setTimeout(() => timeoutController.abort(), API_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
      signal: timeoutController.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiTimeoutError();
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();
  let payload: any = null;

  if (contentType.includes("text/html")) {
    throw new Error(
      "El backend devolvió HTML en lugar de JSON. Revisa VITE_MEDAM_CAREERS_API_URL y la configuración de rutas del hosting.",
    );
  }

  if (text) {
    if (contentType.includes("application/json") || text.trim().startsWith("{" ) || text.trim().startsWith("[")) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      clearAuthSession();
      throw new Error("Tu sesión expiró o no es válida. Inicia sesión nuevamente.");
    }
    const message = payload?.message || (text ? text.slice(0, 180) : "No pudimos completar la solicitud al backend de MEDAM Careers.");
    throw new Error(message);
  }

  return (payload ?? (null as T)) as T;
}
